// src/pages/accounts/TallyExport.jsx
// D-291 P3d-5c-1: purchases come from the vendor's BILL (purchase_bills). The old
//   one-voucher-per-PO export is kept only for POs received before bills existed.
//   Proformas from a vendor with a GSTIN are held until the tax invoice; bills whose
//   lines do not add up are listed, never silently skipped.
// D-291 P3d-5c-2: "Payments & Receipts" = real money movement (tallyMoney.js - the
//   same rule as the Cash / Bank Book): vendor / subcontractor payments with TDS,
//   expense / transport payments, payment reversals, subcontractor bills, money
//   received. Replaces the old export that sent every expense / transport ledger
//   row as cash paid.

import { useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useCompany } from "../../context/CompanyContext";
import {
  generateSalesXML,
  generateMoneyXML,
  generatePurchaseXML,
  generateAllXML,
  downloadXML,
  planPurchaseExport,
  planMoneyExport,
} from "../../services/tallyExportService";

const EXPORT_TYPES = [
  { key: "all",      label: "All Vouchers",          icon: "📦", desc: "Sales + Purchases + Payments & Receipts" },
  { key: "sales",    label: "Sales Vouchers",         icon: "🧾", desc: "From Invoices" },
  { key: "money",    label: "Payments & Receipts",    icon: "💸", desc: "Payments, receipts, reversals, subcontractor bills" },
  { key: "purchase", label: "Purchase Vouchers",      icon: "🛒", desc: "Vendor bills (+ old POs)" },
];

const rupees = (p) => `₹${(p / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const daysSince = (ts) => (ts?.seconds ? Math.max(0, Math.floor((Date.now() - ts.seconds * 1000) / 86400000)) : null);

export default function TallyExport() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const { company } = useCompany();

  const [exportType, setExportType] = useState("all");
  const [dateFrom,   setDateFrom]   = useState("");
  const [dateTo,     setDateTo]     = useState("");
  const [loading,    setLoading]    = useState(false);
  const [preview,    setPreview]    = useState(null); // { invoices, money, purchase }
  const [previewing, setPreviewing] = useState(false);

  // ── Date filter helpers ─────────────────────────────────
  const inDates = (d) => {
    if (!dateFrom && !dateTo) return true;
    if (!d || isNaN(d)) return true; // unparseable → include it
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo   && d > new Date(dateTo + "T23:59:59")) return false;
    return true;
  };
  const inRange = (item) => {
    // Handle Firestore Timestamp, plain Date, ISO string, epoch
    const ts = item.invoice_date?.seconds || item.created_at?.seconds;
    const d  = ts
      ? new Date(ts * 1000)
      : new Date(item.invoice_date || item.created_at || 0);
    return inDates(d);
  };
  // A tax invoice counts on its invoice date; a bill without one on the day it was entered.
  const billInRange = (b) => {
    if (b.bill_type !== "proforma" && b.vendor_invoice_date) return inDates(new Date(b.vendor_invoice_date + "T00:00:00"));
    return inDates(b.created_at?.seconds ? new Date(b.created_at.seconds * 1000) : null);
  };

  // ── Fetch data for preview ──────────────────────────────
  const fetchPreview = async () => {
    if (!companyId) { alert("Company not loaded"); return; }
    if (!company)   { alert("Company profile not loaded"); return; }
    setPreviewing(true);
    try {
      const byCompany = (name) => getDocs(query(collection(db, name), where("company_id", "==", companyId)));
      const [invSnap, ftSnap, logSnap, poSnap, billSnap, rcSnap, clientSnap] = await Promise.all([
        byCompany("invoices"),
        byCompany("financial_transactions"),
        byCompany("subcontractor_work_logs"),
        byCompany("purchase_orders"),
        byCompany("purchase_bills"),
        byCompany("purchase_receipts"),
        byCompany("clients"),
      ]);
      const docs = (snap) => snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const invoices = docs(invSnap).filter(inRange);

      const moneyPlan = planMoneyExport({ rows: docs(ftSnap), logs: docs(logSnap), clients: docs(clientSnap), company });
      const money = {
        vouchers: moneyPlan.vouchers.filter(v => inDates(v.at ? new Date(v.at) : null)),
        problems: moneyPlan.problems,   // all dates - each one needs a look
        skipped:  moneyPlan.skipped,
      };

      const pos      = docs(poSnap).filter(inRange);
      const allBills = docs(billSnap);
      const purchase = planPurchaseExport({
        bills: allBills.filter(billInRange), allBills, pos, receipts: docs(rcSnap), company,
      });

      setPreview({ invoices, money, purchase });
    } catch (err) {
      console.error("Preview error:", err);
      alert("Failed to fetch data");
    } finally { setPreviewing(false); }
  };

  // ── Export ──────────────────────────────────────────────
  const handleExport = async () => {
    if (!preview) { alert("Click Preview first"); return; }
    if (!company) { alert("Company profile not loaded"); return; }

    const config = company; // company document has tally_* fields
    const { invoices, money, purchase } = preview;

    setLoading(true);
    try {
      let xml      = "";
      let filename = "";
      const date   = new Date().toISOString().slice(0, 10);

      switch (exportType) {
        case "sales":
          xml      = generateSalesXML(invoices, company, config);
          filename = `tally_sales_${date}.xml`;
          break;
        case "money":
          xml      = generateMoneyXML(money.vouchers);
          filename = `tally_payments_receipts_${date}.xml`;
          break;
        case "purchase":
          xml      = generatePurchaseXML(purchase, company, config);
          filename = `tally_purchases_${date}.xml`;
          break;
        default:
          xml      = generateAllXML(invoices, money.vouchers, purchase, company, config);
          filename = `tally_all_${date}.xml`;
      }

      downloadXML(xml, filename);
    } catch (err) {
      console.error("Export error:", err);
      alert("Export failed: " + err.message);
    } finally { setLoading(false); }
  };

  const mv = preview ? preview.money.vouchers : [];
  const counts = preview ? {
    invoices: preview.invoices.filter(i => Number(i.total_amount || 0) > 0).length,
    money:    mv.length,
    pos:      preview.purchase.exportBills.length + preview.purchase.legacyPos.length,
  } : null;
  const moneyBreak = preview ? {
    payments: mv.filter(v => v.kind === "payment").length,
    receipts: mv.filter(v => v.kind === "receipt").length,
    journals: mv.filter(v => v.kind === "journal").length,
    noMode:   mv.filter(v => v.noMode).length,
  } : null;
  const held          = preview ? preview.purchase.heldBills : [];
  const problems      = preview ? preview.purchase.problemBills : [];
  const moneyProblems = preview ? preview.money.problems : [];

  return (
    <div className="p-4 bg-gray-50 min-h-screen max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-slate-800 mb-1">Tally Export</h1>
      <p className="text-sm text-slate-500 mb-5">
        Export vouchers for Tally ERP 9 / TallyPrime import
      </p>

      {/* Company check */}
      {company && (!company.state_code || !company.gst_number) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-700">
          ⚠ <strong>Company Profile incomplete</strong> — GST number or State missing.
          <a href="/company-profile" className="underline ml-1">Fill Company Profile →</a>
        </div>
      )}

      <div className="space-y-4">

        {/* EXPORT TYPE */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <label className="text-xs font-semibold text-slate-500 uppercase block mb-3">
            Export Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {EXPORT_TYPES.map(t => (
              <button key={t.key} onClick={() => setExportType(t.key)}
                className={`p-3 rounded-xl border text-left transition ${
                  exportType === t.key
                    ? "border-slate-800 bg-slate-800 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}>
                <div className="text-lg mb-0.5">{t.icon}</div>
                <div className="text-sm font-semibold">{t.label}</div>
                <div className={`text-xs mt-0.5 ${exportType === t.key ? "text-slate-300" : "text-slate-400"}`}>
                  {t.desc}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* DATE RANGE */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <label className="text-xs font-semibold text-slate-500 uppercase block mb-3">
            Date Range <span className="font-normal normal-case text-slate-400">(optional — leave blank for all)</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">From</label>
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPreview(null); }}
                className="w-full border border-slate-200 p-2.5 rounded-xl text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">To</label>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPreview(null); }}
                className="w-full border border-slate-200 p-2.5 rounded-xl text-sm outline-none" />
            </div>
          </div>
        </div>

        {/* PREVIEW BUTTON */}
        <button onClick={fetchPreview} disabled={previewing}
          className="w-full py-3 rounded-xl border-2 border-slate-300 text-slate-700 font-semibold text-sm hover:bg-slate-50 disabled:opacity-40 transition">
          {previewing ? "Loading..." : "🔍 Preview Data"}
        </button>

        {/* PREVIEW COUNTS */}
        {counts && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Data Preview</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Invoices",            count: counts.invoices, icon: "🧾", color: "text-blue-600" },
                { label: "Payments & Receipts", count: counts.money,    icon: "💸", color: "text-red-600" },
                { label: "Purchases",           count: counts.pos,      icon: "🛒", color: "text-purple-600" },
              ].map(({ label, count, icon, color }) => (
                <div key={label} className="text-center p-3 bg-slate-50 rounded-xl">
                  <div className="text-xl">{icon}</div>
                  <div className={`text-2xl font-black mt-1 ${color}`}>{count}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{label}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Payments & Receipts: {moneyBreak.payments} payment(s) · {moneyBreak.receipts} receipt(s) · {moneyBreak.journals} journal(s) (reversals, subcontractor bills).
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Purchases: {preview.purchase.exportBills.length} vendor bill(s) + {preview.purchase.legacyPos.length} old PO(s) received before bills existed.
            </p>
            {moneyBreak.noMode > 0 && (
              <p className="text-xs text-amber-700 mt-1">
                {moneyBreak.noMode} old payment(s) have no payment mode - booked to the Cash ledger; please check them in Tally.
              </p>
            )}

            {held.length > 0 && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                <p className="font-semibold mb-1">{held.length} proforma bill(s) NOT exported - waiting for the vendor's tax invoice</p>
                <ul className="space-y-0.5">
                  {held.map(({ bill, plan }) => {
                    const days = daysSince(bill.created_at);
                    return (
                      <li key={plan.bill_id}>
                        {plan.vendor} · {plan.po_number} · {rupees(plan.totalP)}{days !== null ? ` · ${days} day(s) old` : ""}
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-1">Enter the tax invoice on the bill (Accounts → the bill → Enter tax invoice); it is exported after that.</p>
              </div>
            )}

            {(problems.length > 0 || moneyProblems.length > 0) && (
              <div role="alert" className="mt-3 bg-red-50 border border-red-300 rounded-xl p-3 text-xs text-red-700">
                <p className="font-semibold mb-1">{problems.length + moneyProblems.length} entr(ies) NOT exported - please check them</p>
                <ul className="space-y-0.5">
                  {problems.map(({ plan }) => (
                    <li key={`b_${plan.bill_id}`}>Bill: {plan.vendor} · {plan.po_number} · {plan.ref || "-"}: {plan.reason}</li>
                  ))}
                  {moneyProblems.map((p) => (
                    <li key={`m_${p.id}`}>Money: {p.party || "-"} ({p.id}): {p.reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {(counts.invoices + counts.money + counts.pos) === 0 && (
              <p className="text-center text-slate-400 text-sm mt-3">
                No data found for selected range.
              </p>
            )}
          </div>
        )}

        {/* EXPORT BUTTON */}
        {counts && (counts.invoices + counts.money + counts.pos) > 0 && (
          <button onClick={handleExport} disabled={loading}
            className="w-full py-3.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-semibold text-sm disabled:opacity-50 transition">
            {loading ? "Generating XML..." : "⬇ Download Tally XML File"}
          </button>
        )}

        {/* INSTRUCTIONS */}
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-xs text-blue-700 space-y-2">
          <p className="font-bold text-sm text-blue-800">📋 How to import in Tally</p>
          <ol className="space-y-1.5 list-decimal list-inside">
            <li>Download the XML file above</li>
            <li>Open <strong>Tally ERP 9</strong> or <strong>TallyPrime</strong></li>
            <li>Go to <strong>Gateway of Tally → Import Data → Vouchers</strong></li>
            <li>Select the downloaded XML file</li>
            <li>Tally will import all vouchers automatically</li>
          </ol>
          <div className="mt-2 pt-2 border-t border-blue-100">
            <p className="font-semibold text-blue-800">⚠ Before importing:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Verify ledger names in <a href="/company-profile" className="underline">Company Profile → Tally Mapping</a> match your Tally exactly (incl. Round-off, Purchase Charges, TDS Payable, Transport, Subcontract Charges)</li>
              <li>Client, Vendor and Subcontractor ledgers must exist in Tally before import</li>
              <li>Import purchases before payments (the "All Vouchers" file is already in that order)</li>
              <li>Export each period once - importing the same file twice creates the vouchers twice in Tally</li>
              <li>Take a backup of Tally data before first import</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
}